/**
 *
 * TimeSlot
 *
 */

import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Button } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import classNames from 'classnames';
import useDialog from 'containers/Dialog/useDialog.js';
import Icon from 'components/media/Icon/index.js';
import Spinner from 'components/Spinner/index.jsx';
import DeleteButton from 'components/interaction/DeleteButton/index.jsx';
import { idValues } from 'utils/arrays.js';
import { isSmallScreen } from 'utils/checkMedia.js';
import { shortTimeString } from 'utils/eventTimes.js';
import { isPast, isFuture } from 'utils/date-fns/index.js';
import messages from '../messages.js';
import { EVENT_HEIGHT } from './constants.js';
import { SLOT_ASSIGNED, SLOT_OPEN } from '../../constants.js';
import EventTime from '../../Planner/Period/BlockModal/EventTime.jsx';
import useMeetingLocationType from '../../hooks/useMeetingLocationType.js';
import useSaveTimeslots from '../../hooks/useSaveTimeslots.js';
import { LOCATION_TYPE_PREMISES, LOCATION_TYPE_VIDEO } from '../../Planner/SelectLocationType/constants.js';

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: absolute;
  height: ${EVENT_HEIGHT - 4}px;
  color: white;
  background-color: ${(p) => p.color};
  border-radius: 4px;
  padding: 0 4px;
  & > :nth-child(2) {
    flex-grow: 1;
  }
  &.slot-break {
    color: ${(p) => p.theme.gray};  
    background-color: ${(p) => p.theme.grayLighter};
    text-align: center;
  }
  &.slot-assigned {
    background-color: ${(p) => p.theme.brandSuccess};  
  }
  &:not(.active) {
    opacity: 0.7;
  }
  .btn.btn-link {
    font-weight: bold;
    color: white;
  }
`;

function TimeSlot(props) {
  const {
    slot, block, getColor, active, working, students, sizes, student, lockedMessage, selected,
  } = props;
  const { min, resolution } = sizes;
  const { meeting, set, offset } = block;
  const disabled = Boolean(lockedMessage) || isPast(slot.end);
  const isAssignedHere = slot.hasReservation && slot.meeting.studentId === block.student.id;
  const intl = useIntl();
  const { saveMeetingLocationType, selectMeetingLocationType } = useMeetingLocationType();
  const { updateTimeslot } = useSaveTimeslots();
  const { dlgConfirm } = useDialog();
  const statusClassName = getStatusClassName();
  const ref = useRef();

  useEffect(() => {
    if (selected) {
      ref.current?.scrollIntoView({ alignToTop: true, behavior: 'smooth' });
    }
  }, []);

  function getStatusClassName() {
    if (slot.available) {
      return 'slot-available';
    }
    return isAssignedHere ? 'slot-assigned' : 'slot-break';
  }

  function getMeetingLocationType() {
    const result = set.meetingLocationType;
    if ([LOCATION_TYPE_PREMISES, LOCATION_TYPE_VIDEO].includes(result)) {
      return result;
    }
    return selectMeetingLocationType(meeting.meetingLocationType);
  }

  async function confirmReplace() {
    let result = true;
    if (meeting.details.assigned) {
      const timeslot = meeting.details.slots.map((ts) => shortTimeString(ts, 'PPp')).join(', ');
      result = await dlgConfirm({ message: { ...messages.replaceMeeting, values: { timeslot } } })
        .then(() => true)
        .catch(() => false);
    }
    return result;
  }

  async function handleAssign() {
    const meetingLocationType = await getMeetingLocationType();
    if (!meetingLocationType) {
      return;
    }
    if (meetingLocationType !== meeting.meetingLocationType) {
      await saveMeetingLocationType(set.id, meeting.id, meetingLocationType);
    }
    if (await confirmReplace()) {
      await updateTimeslot(set.id, { ...slot, meetingId: meeting.id, status: SLOT_ASSIGNED })
        .catch((error) => {
          if (error.message !== 'Gone') {
            throw new Error(error);
          }
        });
    }
  }

  async function handleUnassign() {
    updateTimeslot(set.id, { ...slot, meetingId: null, status: SLOT_OPEN });
  }

  function getStyle() {
    const top = ((slot.start.getTime() - min) * resolution) + 2;
    const width = 100 / students.length;
    const left = width * offset;
    return {
      top: `${top}px`,
      left: `calc(${left}% + 5px)`,
      width: `calc(${width}% - 10px)`,
    };
  }

  function renderAvailable() {
    if (!active) {
      return <FormattedMessage {...messages.available} />;
    }
    if (idValues(set.meetings).every((mt) => mt.locked || mt.studentId !== student.userId)) {
      return <FormattedMessage {...messages.assigned} />;
    }
    if (isPast(set.registerClose)) {
      return <FormattedMessage {...messages.alreadyClosed} />;
    }
    if (isFuture(set.registerStart)) {
      return <FormattedMessage {...messages.notStarted} />;
    }
    return (
      <Button variant="link" className="p-0 slot-action" disabled={disabled} onClick={handleAssign}>
        <FormattedMessage {...messages.select} />
      </Button>
    );
  }

  function renderAction() {
    if (slot.available) {
      return renderAvailable();
    }
    if (!isAssignedHere) {
      return <FormattedMessage {...messages.occupied} />;
    }
    if (slot.locked) {
      return (
        <>
          <FormattedMessage {...messages.yourTime} />
          <Icon variant="link" className="ss-lock" />
        </>
      );
    }
    const isOpen = isPast(set.registerStart) && (!set.registerEnd || isFuture(set.registerEnd));
    return (
      <>
        <span>
          <Icon className="ss-checkmark" space inline />
          <FormattedMessage {...messages.yourTime} />
        </span>
        {active && isOpen
          && (
            <DeleteButton
              aria-label="unassign"
              disabled={disabled}
              onClick={handleUnassign}
              confirmText={messages.removeSlot}
            />
          )}
      </>
    );
  }

  const title = disabled ? intl.formatMessage(lockedMessage || messages.alreadyPast) : block.set.title;
  const color = getColor(block.student);
  const className = classNames(statusClassName, { active });
  return (
    <Wrapper ref={ref} style={getStyle()} className={className} color={color} title={title}>
      <EventTime {...slot} compact={isSmallScreen()} className="me-3" />
      {working ? <Spinner size="24px" /> : renderAction()}
    </Wrapper>
  );
}

TimeSlot.propTypes = {
  students: PropTypes.array,
  active: PropTypes.bool,
  lockedMessage: PropTypes.object,
  student: PropTypes.object,
  working: PropTypes.bool,
  selected: PropTypes.bool,
  sizes: PropTypes.object,
  slot: PropTypes.object,
  getColor: PropTypes.func,
  block: PropTypes.object,
};

export default TimeSlot;
